import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Hero from '../../components/Hero';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faExternalLinkAlt, faTag } from '@fortawesome/free-solid-svg-icons';
import { faGraduationCap, faChalkboardTeacher, faChess, faChild, faCalendarAlt, faBook } from '@fortawesome/free-solid-svg-icons';
import { getApplicationBySlug, applications } from '../../data/applications';
import applicationsHeroImage from '../../assets/images/duyurular.jpg';
import '../ApplicationPages/ApplicationDetail.css';

const ApplicationDetailEN = () => {
  const { slug } = useParams();
  const application = getApplicationBySlug(slug);

  // Translate application data
  const translateApp = (app) => {
    if (!app) return null;
    
    const translations = {
      'progenx-academy': {
        title: 'ProgenX Academy',
        description: 'Education technology application',
        shortDescription: 'AI-powered personalized learning platform',
        category: 'Education Technologies',
        content: `ProgenX Academy is an innovative platform that offers AI-powered personalized learning experiences and creates optimized educational programs according to students' individual learning needs.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• AI-powered personalized learning paths</li>
  <li style="margin-bottom: 10px;">• Smart question-solving system with instant feedback</li>
  <li style="margin-bottom: 10px;">• Detailed progress tracking and performance analysis</li>
  <li style="margin-bottom: 10px;">• Student-specific learning plans</li>
  <li style="margin-bottom: 10px;">• Fun and interactive content</li>
</ul>

With ProgenX Academy, every student learns at their own pace and maximizes their potential.`
      },
      'ozel-ders': {
        title: 'Private Tutoring Platform',
        description: 'Platform connecting students seeking private lessons with teachers',
        shortDescription: 'Platform connecting students seeking private lessons with teachers',
        category: 'Education Platform',
        content: `Private Tutoring Platform is a platform that connects students seeking private lessons with teachers, offering a reliable and effective education solution.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• Student and teacher matching system</li>
  <li style="margin-bottom: 10px;">• Reliable teacher profiles and reviews</li>
  <li style="margin-bottom: 10px;">• Flexible lesson scheduling</li>
  <li style="margin-bottom: 10px;">• Online and in-person lesson options</li>
  <li style="margin-bottom: 10px;">• Secure payment system</li>
</ul>

With Private Tutoring Platform, students find teachers that suit their needs, and teachers reach their students.`
      },
      'ib-egitimi': {
        title: 'IB Education Platform',
        description: 'Comprehensive platform related to IB education',
        shortDescription: 'Comprehensive platform related to IB education',
        category: 'International Education',
        content: `IB Education Platform is a comprehensive education platform specially designed for International Baccalaureate (IB) program students and teachers.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• IB curriculum-compliant content</li>
  <li style="margin-bottom: 10px;">• IB exam preparation materials</li>
  <li style="margin-bottom: 10px;">• Resources for IB teachers</li>
  <li style="margin-bottom: 10px;">• Support tools for IB students</li>
</ul>

With IB Education Platform, IB program students and teachers access all the resources they need from a single place.`
      },
      'chess': {
        title: 'ProgenX Chess',
        description: 'Chess game',
        shortDescription: 'Educational and fun chess application',
        category: 'Game',
        content: `ProgenX Chess is an educational and fun chess application designed for everyone who wants to learn and improve chess.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• Chess learning modules</li>
  <li style="margin-bottom: 10px;">• Computer opponents at different levels</li>
  <li style="margin-bottom: 10px;">• Play matches with online players</li>
  <li style="margin-bottom: 10px;">• Chess tactics and strategies</li>
  <li style="margin-bottom: 10px;">• Progress tracking and statistics</li>
</ul>

Learn chess, practice, and improve yourself with ProgenX Chess.`
      },
      'kids': {
        title: 'ProgenX Kids',
        description: 'Educational application for children',
        shortDescription: 'Fun and educational application for children',
        category: 'Children Education',
        content: `ProgenX Kids is a fun and educational application specially designed for children. It allows children to have fun while learning.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• Age-appropriate educational content</li>
  <li style="margin-bottom: 10px;">• Fun games and activities</li>
  <li style="margin-bottom: 10px;">• Safe and child-friendly interface</li>
  <li style="margin-bottom: 10px;">• Parent control panel</li>
  <li style="margin-bottom: 10px;">• Interactive learning experiences</li>
</ul>

With ProgenX Kids, your children learn and grow while having fun.`
      },
      'program': {
        title: 'Class Schedule Platform',
        description: 'Platform for schools to create their own schedules',
        shortDescription: 'Class schedule creation platform for schools',
        category: 'School Management',
        content: `Class Schedule Platform is a platform where schools can easily create, manage, and optimize class schedules for teachers and classes.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• Automatic class schedule creation</li>
  <li style="margin-bottom: 10px;">• Teacher and class management</li>
  <li style="margin-bottom: 10px;">• Conflict checking and optimization</li>
  <li style="margin-bottom: 10px;">• Flexible schedule editing</li>
  <li style="margin-bottom: 10px;">• Reporting and analysis tools</li>
</ul>

With Class Schedule Platform, schools efficiently manage class schedules and optimize teacher-student compatibility.`
      },
      'edtech': {
        title: 'EdTech Content Platform',
        description: 'Platform for teachers to develop content',
        shortDescription: 'Content development platform for teachers',
        category: 'Content Development',
        content: `EdTech Content Platform is a comprehensive platform where teachers can develop, share, and manage educational content.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• Interactive content creation tools</li>
  <li style="margin-bottom: 10px;">• Multi-media support (video, audio, visual)</li>
  <li style="margin-bottom: 10px;">• Content sharing and collaboration</li>
  <li style="margin-bottom: 10px;">• SCORM-compliant content</li>
  <li style="margin-bottom: 10px;">• Student progress tracking</li>
</ul>

With EdTech Content Platform, teachers create professional educational content and present it to their students.`
      },
      'imat': {
        title: 'IMAT Platform',
        description: 'IMAT education and assessment platform',
        shortDescription: 'Specialized preparation platform for IMAT exam',
        category: 'International Education',
        content: `IMAT Platform is a comprehensive preparation platform specially designed for students who want to study medicine in Italy.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• IMAT exam-specific preparation materials</li>
  <li style="margin-bottom: 10px;">• Practice exams and performance analysis</li>
  <li style="margin-bottom: 10px;">• Detailed topic-based study modules</li>
  <li style="margin-bottom: 10px;">• English and Italian language support</li>
  <li style="margin-bottom: 10px;">• Live lessons from expert teachers</li>
  <li style="margin-bottom: 10px;">• Progress tracking and personalized recommendations</li>
</ul>

Turn your dream of studying medicine in Italy into reality with IMAT Platform.`
      },
      'alevel': {
        title: 'A-Level Platform',
        description: 'Comprehensive education platform for A-Level exam preparation',
        shortDescription: 'Specialized preparation platform for A-Level exams',
        category: 'International Education',
        content: `A-Level Platform is a comprehensive education platform that provides preparation for A-Level exams for students who want to study at universities in the UK and other countries.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• A-Level curriculum-compliant content</li>
  <li style="margin-bottom: 10px;">• Preparation materials for all A-Level subjects</li>
  <li style="margin-bottom: 10px;">• Past exam questions and solutions</li>
  <li style="margin-bottom: 10px;">• Practice exams and performance analysis</li>
  <li style="margin-bottom: 10px;">• Live lessons from expert teachers</li>
  <li style="margin-bottom: 10px;">• Progress tracking and personalized learning plans</li>
</ul>

Prepare for A-Level exams in the best way possible with A-Level Platform for university education in the UK and worldwide.`
      },
      'sat': {
        title: 'SAT Platform',
        description: 'Comprehensive education platform for SAT exam preparation',
        shortDescription: 'Specialized preparation platform for SAT exam',
        category: 'International Education',
        content: `SAT Platform is a comprehensive education platform that provides preparation for the SAT exam for students who want to study at universities in the United States and other countries.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• SAT exam-specific preparation materials</li>
  <li style="margin-bottom: 10px;">• Detailed content for Math, Reading, and Writing sections</li>
  <li style="margin-bottom: 10px;">• Past SAT questions and solutions</li>
  <li style="margin-bottom: 10px;">• Practice exams and performance analysis</li>
  <li style="margin-bottom: 10px;">• Strategy and tips with exam techniques</li>
  <li style="margin-bottom: 10px;">• Progress tracking and personalized learning plans</li>
</ul>

Prepare for the SAT exam in the best way possible with SAT Platform for university education in the United States.`
      },
      'ap': {
        title: 'AP Platform',
        description: 'Comprehensive education platform for Advanced Placement (AP) exam preparation',
        shortDescription: 'Specialized preparation platform for AP exams',
        category: 'International Education',
        content: `AP Platform is a comprehensive education platform that provides preparation for Advanced Placement (AP) exams for students who want to study at universities in the United States and other countries.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• Preparation materials for all AP subjects</li>
  <li style="margin-bottom: 10px;">• AP Calculus, AP Biology, AP Chemistry, AP Physics, and more</li>
  <li style="margin-bottom: 10px;">• Past AP exam questions and solutions</li>
  <li style="margin-bottom: 10px;">• Practice exams and performance analysis</li>
  <li style="margin-bottom: 10px;">• Live lessons from expert teachers</li>
  <li style="margin-bottom: 10px;">• Progress tracking and personalized learning plans</li>
</ul>

Prepare for AP exams in the best way possible with AP Platform for university education in the United States and earn college credit.`
      },
      'act': {
        title: 'ACT Platform',
        description: 'Comprehensive education platform for ACT exam preparation',
        shortDescription: 'Specialized preparation platform for ACT exam',
        category: 'International Education',
        content: `ACT Platform is a comprehensive education platform that provides preparation for the ACT exam for students who want to study at universities in the United States and other countries.

<h3 style="color: var(--primary-color); margin: 25px 0 15px;">🎯 Features:</h3>
<ul style="list-style-type: none; padding-left: 0;">
  <li style="margin-bottom: 10px;">• ACT exam-specific preparation materials</li>
  <li style="margin-bottom: 10px;">• Detailed content for English, Math, Reading, and Science sections</li>
  <li style="margin-bottom: 10px;">• Past ACT questions and solutions</li>
  <li style="margin-bottom: 10px;">• Practice exams and performance analysis</li>
  <li style="margin-bottom: 10px;">• Strategy and tips with exam techniques</li>
  <li style="margin-bottom: 10px;">• Progress tracking and personalized learning plans</li>
</ul>

Prepare for the ACT exam in the best way possible with ACT Platform for university education in the United States.`
      }
    };
    
    const translation = translations[app.id];
    if (translation) {
      return {
        ...app,
        title: translation.title,
        description: translation.description,
        shortDescription: translation.shortDescription,
        category: translation.category,
        content: translation.content || app.content
      };
    }
    return app;
  };

  const translatedApp = translateApp(application);

  if (!application) {
    return (
      <div className="application-detail">
        <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
          <h1>Application Not Found</h1>
          <p>The application you are looking for could not be found.</p>
          <Link to="/en/applications" className="btn btn-primary">
            Back to Applications
          </Link>
        </div>
      </div>
    );
  }

  const getIcon = (iconName) => {
    const icons = {
      academy: faGraduationCap,
      tutor: faChalkboardTeacher,
      ib: faBook,
      chess: faChess,
      kids: faChild,
      program: faCalendarAlt,
      edtech: faBook
    };
    return icons[iconName] || faGraduationCap;
  };

  // Find previous and next applications
  const currentIndex = applications.findIndex(a => a.slug === slug);
  const previousApplication = currentIndex > 0 ? applications[currentIndex - 1] : null;
  const nextApplication = currentIndex < applications.length - 1 ? applications[currentIndex + 1] : null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": translatedApp.title,
    "description": translatedApp.description,
    "url": application.url,
    "applicationCategory": translatedApp.category,
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "TRY"
    }
  };

  const faqData = application.faqs || [];
  // Translate FAQs
  const translatedFaqs = faqData.map(faq => {
    const translations = {
      'ProgenX Academy nedir?': {
        question: 'What is ProgenX Academy?',
        answer: 'ProgenX Academy is an AI-powered personalized learning platform. It creates learning paths according to students\' individual needs and provides smart feedback.'
      },
      'ProgenX Academy nasıl çalışır?': {
        question: 'How does ProgenX Academy work?',
        answer: 'The platform analyzes student performance to create personalized learning plans. Thanks to AI technology, each student learns at their own pace and style.'
      },
      'Özel Ders Platformu nasıl çalışır?': {
        question: 'How does Private Tutoring Platform work?',
        answer: 'The platform matches students\' needs with teachers\' areas of expertise. Students can find suitable teachers, and teachers can reach their students.'
      },
      'IB Eğitimi Platformu kimler için?': {
        question: 'Who is IB Education Platform for?',
        answer: 'IB Education Platform is designed for IB program students, teachers, and anyone who wants to receive IB education.'
      },
      'ProgenX Chess kimler için uygun?': {
        question: 'Who is ProgenX Chess suitable for?',
        answer: 'ProgenX Chess is suitable for beginner players who want to learn chess and experienced players who want to improve themselves.'
      },
      'ProgenX Kids hangi yaş grubu için?': {
        question: 'What age group is ProgenX Kids for?',
        answer: 'ProgenX Kids is designed for preschool and elementary school children. It offers age-appropriate content and activities.'
      },
      'Ders Programı Platformu nasıl çalışır?': {
        question: 'How does Class Schedule Platform work?',
        answer: 'The platform takes teacher, class, and course information from schools and automatically creates optimized class schedules. It prevents conflicts and offers the most efficient schedule.'
      },
      'EdTech İçerik Platformu kimler için?': {
        question: 'Who is EdTech Content Platform for?',
        answer: 'EdTech Content Platform is designed for teachers, educational institutions, and content developers who want to develop educational content.'
      },
      'İMAT Platformu nedir?': {
        question: 'What is IMAT Platform?',
        answer: 'IMAT Platform is a comprehensive educational platform specially designed for preparation for the IMAT exam, which is the entrance exam for medical faculties in Italy.'
      },
      'İMAT Platformu kimler için uygun?': {
        question: 'Who is IMAT Platform suitable for?',
        answer: 'IMAT Platform is suitable for all students who want to study medicine in Italy and are preparing for the IMAT exam.'
      },
      'A-Level Platformu nedir?': {
        question: 'What is A-Level Platform?',
        answer: 'A-Level Platform is a comprehensive education platform that provides preparation for A-Level exams for students who want to study at universities in the UK and other countries.'
      },
      'A-Level Platformu kimler için uygun?': {
        question: 'Who is A-Level Platform suitable for?',
        answer: 'A-Level Platform is suitable for all students who want to study at universities in the UK or countries that use the A-Level system and are preparing for A-Level exams.'
      },
      'SAT Platformu nedir?': {
        question: 'What is SAT Platform?',
        answer: 'SAT Platform is a comprehensive education platform that provides preparation for the SAT exam for students who want to study at universities in the United States and other countries.'
      },
      'SAT Platformu kimler için uygun?': {
        question: 'Who is SAT Platform suitable for?',
        answer: 'SAT Platform is suitable for all students who want to study at universities in the United States or countries that accept the SAT exam and are preparing for the SAT exam.'
      },
      'AP Platformu nedir?': {
        question: 'What is AP Platform?',
        answer: 'AP Platform is a comprehensive education platform that provides preparation for Advanced Placement (AP) exams for students who want to study at universities in the United States and other countries.'
      },
      'AP Platformu kimler için uygun?': {
        question: 'Who is AP Platform suitable for?',
        answer: 'AP Platform is suitable for all students who want to study at universities in the United States or countries that accept AP exams and are preparing for AP exams.'
      },
      'ACT Platformu nedir?': {
        question: 'What is ACT Platform?',
        answer: 'ACT Platform is a comprehensive education platform that provides preparation for the ACT exam for students who want to study at universities in the United States and other countries.'
      },
      'ACT Platformu kimler için uygun?': {
        question: 'Who is ACT Platform suitable for?',
        answer: 'ACT Platform is suitable for all students who want to study at universities in the United States or countries that accept the ACT exam and are preparing for the ACT exam.'
      }
    };
    
    const translation = translations[faq.question];
    return translation ? { question: translation.question, answer: translation.answer } : faq;
  });

  const faqStructuredData = translatedFaqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": translatedFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  return (
    <div className="application-detail">
      <Helmet>
        <title>{translatedApp.title} | ProgenX Applications</title>
        <meta name="description" content={translatedApp.description} />
        <meta name="keywords" content={`progenx, ${application.tags ? application.tags.join(', ') : ''}, ${translatedApp.category}, application`} />
        <meta property="og:title" content={translatedApp.title} />
        <meta property="og:description" content={translatedApp.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://progenxacademy.com/en/applications/${application.slug}`} />
        <link rel="canonical" href={`https://progenxacademy.com/en/applications/${application.slug}`} />
        <link rel="alternate" hreflang="tr" href={`https://progenxacademy.com/uygulamalar/${application.slug}`} />
        <link rel="alternate" hreflang="en" href={`https://progenxacademy.com/en/applications/${application.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        {faqStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(faqStructuredData)}
          </script>
        )}
      </Helmet>
      
      <Hero 
        title={translatedApp.title} 
        backgroundImage={applicationsHeroImage}
      />
      
      <section className="application-detail-content">
        <div className="container">
          <Link to="/en/applications" className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to All Applications
          </Link>

          <article className="application-article">
            <div className="application-header">
              <div className="app-icon-large">
                <FontAwesomeIcon icon={getIcon(application.icon)} />
              </div>
              <div className="application-header-text">
                <div className="application-meta">
                  <span className="application-category">
                    <FontAwesomeIcon icon={faTag} /> {translatedApp.category}
                  </span>
                </div>
                <h1 className="application-title">{translatedApp.title}</h1>
                <p className="application-short-description">{translatedApp.shortDescription}</p>
              </div>
            </div>

            <div 
              className="application-content"
              dangerouslySetInnerHTML={{ __html: translatedApp.content || application.content }} 
            />

            <div className="application-cta">
              <a 
                href={application.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Go to Application
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </a>
            </div>

            {application.tags && application.tags.length > 0 && (
              <div className="application-tags">
                {application.tags.map((tag, index) => (
                  <span key={index} className="tag">#{tag}</span>
                ))}
              </div>
            )}

            {translatedFaqs.length > 0 && (
              <section className="application-faq">
                <h2>Frequently Asked Questions</h2>
                {translatedFaqs.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <h3 className="faq-question">{faq.question}</h3>
                    <p className="faq-answer">{faq.answer}</p>
                  </div>
                ))}
              </section>
            )}
          </article>

          <div className="application-navigation">
            {previousApplication && (() => {
              const prevTranslated = translateApp(previousApplication);
              return (
                <Link to={`/en/applications/${previousApplication.slug}`} className="nav-link prev">
                  <FontAwesomeIcon icon={faArrowLeft} />
                  <div>
                    <span className="nav-label">Previous</span>
                    <span className="nav-title">{prevTranslated.title}</span>
                  </div>
                </Link>
              );
            })()}
            {nextApplication && (() => {
              const nextTranslated = translateApp(nextApplication);
              return (
                <Link to={`/en/applications/${nextApplication.slug}`} className="nav-link next">
                  <div>
                    <span className="nav-label">Next</span>
                    <span className="nav-title">{nextTranslated.title}</span>
                  </div>
                  <FontAwesomeIcon icon={faArrowLeft} style={{ transform: 'rotate(180deg)' }} />
                </Link>
              );
            })()}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApplicationDetailEN;
